import type { RenderPlan } from '@tsagko/label-zpl-pipeline'
import { A0_VMETRICS } from '@tsagko/label-zpl-pipeline'
import type { VariableMeta } from '@tsagko/label-contracts'
import { useMemo, type CSSProperties } from 'react'
import {
  CANVAS_TEXT_FONT_FAMILY,
  CANVAS_TEXT_FONT_WEIGHT,
  capRefFrac,
  measureCanvasFontVMetrics,
} from './font-vmetrics'
import { unpack1BitToRgba } from './rasterize-image'

interface RenderPlanItemProps {
  plan: RenderPlan
  pxPerMm: number
  /**
   * Variable metadata for the block being rendered. Threaded down from
   * `Canvas` (Task 13 of the block-variables-and-region-table slice). The
   * current pipeline pre-resolves text into `plan.text` so this prop is
   * accepted for forward-compat with Task 14 (which will switch the plan
   * builder to use `resolveContent` against this map) without changing the
   * runtime output here.
   */
  variables?: Record<string, VariableMeta>
  /**
   * Sample / live data record. Same forward-compat note as `variables`.
   * `undefined` on the authoring canvas — defaults will flow through once
   * Task 14 wires `resolveContent` into the plan builder.
   */
  data?: Record<string, unknown>
}

/**
 * Resolve the displayed text for a text plan. Plans emitted by the current
 * pipeline pre-resolve `plan.text` via the legacy ValueSource path, so we
 * return it verbatim. When Task 14 lands and the plan-builder switches to
 * carrying unresolved `content` strings, this is the seam where we hand off
 * to `resolveContent` from `@tsagko/label-zpl-pipeline`.
 */
function resolveTextForPlan(
  plan: Extract<RenderPlan, { kind: 'text' }>,
  _variables: Record<string, VariableMeta> | undefined,
  _data: Record<string, unknown> | undefined,
): string {
  return plan.text
}

const ALIGNMENT_CSS: Record<'L' | 'C' | 'R' | 'J', CSSProperties['textAlign']> = {
  L: 'left',
  C: 'center',
  R: 'right',
  J: 'justify',
}

const ROTATION_CSS: Record<'N' | 'R' | 'I' | 'B', string | undefined> = {
  N: undefined,
  R: 'rotate(90deg)', // clockwise — CSS positive angle, y-down screen coords
  I: 'rotate(180deg)',
  B: 'rotate(270deg)',
}

/**
 * Render one RenderPlan as a positioned HTML element. The single source
 * of truth for tier-1 designer canvas presentation. No CSS flex-centering;
 * anchors are top-left in mm, mirroring the ZPL emitter's `^FO` semantics.
 *
 * Every rendered element carries `data-render-plan-id={plan.id}` so the
 * Layer 3 parity test can query it.
 */
export function RenderPlanItem({ plan, pxPerMm, variables, data }: RenderPlanItemProps) {
  if (plan.kind === 'text') {
    const displayed = resolveTextForPlan(plan, variables, data)
    // Mirror the ZPL emitter's `^FR` reverse-field behaviour: text drawn on
    // a black-background region prints white on the label, so it must show
    // white on the authoring canvas too — otherwise stone-900-on-black is
    // effectively invisible.
    const onBlackRegion = plan.regionBackgroundColor === 'black'
    const vAlign = plan.vAlign ?? 'top'
    const canvasVm = measureCanvasFontVMetrics()
    // Translate the pipeline's ^A0 cell position (the plan anchor) to the
    // canvas font's cell position by aligning the PINNED reference line
    // (cap-top for 'top', cap-center for 'middle', baseline for 'bottom' —
    // `plan.vAlign` carries which one the pipeline pinned). The correction
    // acts along the text's LOCAL vertical axis, which maps to a different
    // page axis per orientation (see the switch below). For unrotated text
    // this equals capBandAnchorY(region, canvasVm) and stays correct for
    // localized-rich-text variants (anchorYMm carries the variant-correct Y;
    // `regionYMm` is the outer zone and must NOT be reused).
    const refDeltaMm =
      (capRefFrac(vAlign, A0_VMETRICS) - capRefFrac(vAlign, canvasVm)) *
      plan.fontHeightMm
    // Print width parity: the ZPL emitter sends `^A0,h,w` and the printer's
    // glyph advance is `advanceEm × w` (linear in w, INDEPENDENT of h —
    // measured via render probe 2026-06-11). The bundled Roboto Condensed
    // Bold is a near-metric match for ^A0 at natural width (w = h), so the
    // plan's w/h ratio is exactly the horizontal condensation the canvas
    // needs. Lay text out in an inflated wrap box (w/scale) with natural
    // glyphs, then scaleX about the top-left: the box condenses back to the
    // true wrap width, and ^FB-style alignment/wrapping inside it condenses
    // with it — matching what the printer draws.
    const widthScale =
      plan.fontHeightMm > 0 && plan.fontWidthMm > 0
        ? plan.fontWidthMm / plan.fontHeightMm
        : 1
    // Rotated-box corner math. The plan anchor is the TOP-LEFT of the
    // rotated text block's page bounding box (probe-verified ^FO+^FB
    // convention — see textPlanGeometry in the pipeline). The CSS span is
    // laid out unrotated with its own top-left at (left, top) and then
    // composed `rotate(θ) scaleX(s)` about that corner, so (left, top) must
    // be the page position of the span's LOCAL top-left corner AFTER
    // rotation, with `refDeltaMm` applied along the local v axis:
    //   N: local origin = page box top-left  → (FO.x, FO.y + Δ)
    //   R: rotate(90°) puts the box LEFT of the origin: the local origin is
    //      the box's top-RIGHT → (FO.x + h − Δ, FO.y)
    //   I: rotate(180°) puts the box UP-LEFT: local origin = box
    //      bottom-right → (FO.x + W, FO.y + h − Δ)
    //   B: rotate(270°) puts the box ABOVE: local origin = box
    //      bottom-left → (FO.x + Δ, FO.y + W)
    // where h = fontHeightMm (cell height) and W = wrapBoxWidthMm (the
    // flow-axis block length; the pipeline already set it to the region
    // height for R/B). The sign of Δ flips with the axis the local v maps
    // to, matching the pipeline's (h − baseline) vs capTop band offsets.
    //
    // KNOWN LIMITATION (maxLines > 1 + rotation): the corner math uses the
    // single-line cell height h, but the printer stacks extra lines page-
    // DOWN (I) / page-RIGHT (R) from ^FO regardless of rotation, while CSS
    // stacks them along local-down (page-UP for I, page-LEFT for R). For
    // n>1 the canvas block is displaced by up to (n−1)·h and line ORDER
    // reverses vs print. Print is correct (the promise that matters);
    // revisit if rotated multi-line text becomes a real use case.
    const hMm = plan.fontHeightMm
    const wMm = plan.wrapBoxWidthMm
    let leftMm: number
    let topMm: number
    switch (plan.rotation) {
      case 'R':
        leftMm = plan.anchorXMm + hMm - refDeltaMm
        topMm = plan.anchorYMm
        break
      case 'I':
        leftMm = plan.anchorXMm + wMm
        topMm = plan.anchorYMm + hMm - refDeltaMm
        break
      case 'B':
        leftMm = plan.anchorXMm + refDeltaMm
        topMm = plan.anchorYMm + wMm
        break
      default:
        leftMm = plan.anchorXMm
        topMm = plan.anchorYMm + refDeltaMm
    }
    const transforms = [
      ROTATION_CSS[plan.rotation],
      widthScale !== 1 ? `scaleX(${widthScale})` : undefined,
    ].filter(Boolean)
    const style: CSSProperties = {
      position: 'absolute',
      left: leftMm * pxPerMm,
      top: topMm * pxPerMm,
      width: (plan.wrapBoxWidthMm / widthScale) * pxPerMm,
      fontSize: `${plan.fontHeightMm * pxPerMm}px`,
      lineHeight: 1,
      textAlign: ALIGNMENT_CSS[plan.alignment],
      transform: transforms.length > 0 ? transforms.join(' ') : undefined,
      transformOrigin: 'top left',
      fontFamily: CANVAS_TEXT_FONT_FAMILY,
      fontWeight: CANVAS_TEXT_FONT_WEIGHT,
      color: onBlackRegion ? 'white' : 'inherit',
      whiteSpace: plan.maxLines > 1 ? 'normal' : 'nowrap',
      overflow: 'hidden',
    }
    return (
      <span
        data-testid={`render-plan-item-${plan.id}`}
        data-render-plan-id={plan.id}
        data-plan-kind={plan.kind}
        style={style}
      >
        {displayed}
      </span>
    )
  }

  if (plan.kind === 'separator') {
    return (
      <div
        data-testid={`render-plan-item-${plan.id}`}
        data-render-plan-id={plan.id}
        data-plan-kind={plan.kind}
        style={{
          position: 'absolute',
          left: plan.anchorXMm * pxPerMm,
          top: plan.anchorYMm * pxPerMm,
          width: plan.widthMm * pxPerMm,
          height: plan.heightMm * pxPerMm,
          backgroundColor: 'currentColor',
        }}
      />
    )
  }

  if (plan.kind === 'image') {
    return <ImagePlanItem plan={plan} pxPerMm={pxPerMm} />
  }

  if (plan.kind === 'barcode') {
    // The plan box is the SYMBOL BOX (quiet zones included). Draw the bars
    // inset by the quiet-zone margins — exactly where print puts them (the
    // ZPL emitter offsets ^FO the same way). The quiet zone itself is just
    // whitespace; the dashed outline marks the full symbol box.
    const qL = (plan.quietLeftMm ?? 0) * pxPerMm
    const qR = (plan.quietRightMm ?? 0) * pxPerMm
    const qT = (plan.quietTopMm ?? 0) * pxPerMm
    const qB = (plan.quietBottomMm ?? 0) * pxPerMm
    return (
      <div
        data-testid={`render-plan-item-${plan.id}`}
        data-render-plan-id={plan.id}
        data-plan-kind={plan.kind}
        style={{
          position: 'absolute',
          left: plan.anchorXMm * pxPerMm,
          top: plan.anchorYMm * pxPerMm,
          width: plan.widthMm * pxPerMm,
          height: plan.heightMm * pxPerMm,
          outline: '1px dashed currentColor',
          outlineOffset: -1,
        }}
      >
        <div
          data-testid={`render-plan-item-${plan.id}-bars`}
          style={{
            position: 'absolute',
            left: qL,
            top: qT,
            width: plan.widthMm * pxPerMm - qL - qR,
            height: plan.heightMm * pxPerMm - qT - qB,
            backgroundImage:
              'repeating-linear-gradient(90deg, currentColor 0, currentColor 2px, transparent 2px, transparent 5px)',
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            fontSize: 8,
            background: 'rgba(255,255,255,0.85)',
            padding: '0 2px',
            borderRadius: 2,
          }}
        >
          {plan.placeholderLabel
            ? `${plan.symbology} · ${plan.placeholderLabel}`
            : plan.symbology}
        </span>
      </div>
    )
  }

  // border-line plans are ZPL-only (emitted by generate-zpl.ts for
  // commandsFromPlans and never put into the canvas plans array); the canvas
  // draws borders via collectRegionBorders in Canvas.tsx instead.
  if (plan.kind === 'border-line') return null

  // Exhaustive: every RenderPlan kind is handled above.
  const _exhaustive: never = plan
  return null
}

function ImagePlanItem({
  plan,
  pxPerMm,
}: {
  plan: Extract<RenderPlan, { kind: 'image' }>
  pxPerMm: number
}) {
  const dataUrl = useMemo(() => {
    if (!plan.buffer || !plan.widthDots || !plan.heightDots) return null
    const rgba = unpack1BitToRgba(plan.buffer, plan.widthDots, plan.heightDots)
    const canvas = document.createElement('canvas')
    canvas.width = plan.widthDots
    canvas.height = plan.heightDots
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    // Cast: unpack1BitToRgba returns Uint8ClampedArray<ArrayBufferLike>, but
    // ImageData constructor requires Uint8ClampedArray<ArrayBuffer>. The
    // buffer is always a plain ArrayBuffer at runtime; the strict lib type
    // includes SharedArrayBuffer which ImageData doesn't accept — safe cast.
    ctx.putImageData(new ImageData(rgba as Uint8ClampedArray<ArrayBuffer>, plan.widthDots, plan.heightDots), 0, 0)
    return canvas.toDataURL('image/png')
  }, [plan.buffer, plan.widthDots, plan.heightDots])

  const baseStyle: CSSProperties = {
    position: 'absolute',
    left: plan.anchorXMm * pxPerMm,
    top: plan.anchorYMm * pxPerMm,
    width: plan.widthMm * pxPerMm,
    height: plan.heightMm * pxPerMm,
  }

  if (!dataUrl) {
    return (
      <div
        data-testid={`render-plan-item-${plan.id}`}
        data-render-plan-id={plan.id}
        data-plan-kind={plan.kind}
        style={{
          ...baseStyle,
          outline: '1px dashed currentColor',
          outlineOffset: -1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 9,
          opacity: 0.6,
        }}
      >
        {plan.placeholderLabel ?? 'image unresolved'}
      </div>
    )
  }
  return (
    <img
      data-testid={`render-plan-item-${plan.id}`}
      data-render-plan-id={plan.id}
      data-plan-kind={plan.kind}
      src={dataUrl}
      alt={`image ${plan.id}`}
      style={{ ...baseStyle, objectFit: 'fill', imageRendering: 'pixelated' }}
    />
  )
}
